import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Users, CalendarHeart, Network, Check, Plus, X, Star, Mail, Send, Loader2, Globe2 } from "lucide-react";

const fmt = (s) => { if (!s) return "Date TBD"; const d = new Date(s); return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); };

export default function GroupsSection() {
  const [groups, setGroups] = useState([]);
  const [myState, setMyState] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/groups").then((r) => { setGroups(r.data.groups); setMyState(r.data.my_state || ""); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (g, e) => {
    e.stopPropagation();
    try {
      await api.post(`/groups/${g.id}/${g.is_member ? "leave" : "join"}`);
      toast.success(g.is_member ? `Left ${g.name}` : `Joined ${g.name}`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (groups.length === 0) return null;

  const inMyState = (g) => myState && (g.states || []).some((s) => String(s).toUpperCase() === myState);
  const visible = showAll ? groups : groups.filter(inMyState);

  return (
    <section className="mt-9" data-testid="groups-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2.5"><span className="inline-block h-6 w-1 rounded-full bg-accent" /><Network className="h-5 w-5 text-primary" /> Chapters &amp; Groups</h2>
        <button data-testid="groups-toggle-all" onClick={() => setShowAll((v) => !v)}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border hover:bg-muted transition-colors">
          {showAll ? <><MapPin className="h-3.5 w-3.5" /> {myState ? "Show groups in my area" : "Show my area"}</> : <><Globe2 className="h-3.5 w-3.5" /> Show all groups</>}
        </button>
      </div>
      {!showAll && myState && visible.length > 0 && (
        <p className="text-sm text-muted-foreground -mt-2 mb-4" data-testid="groups-area-note">Showing chapters for your area ({myState}).</p>
      )}
      {!showAll && visible.length === 0 && (
        <div className="bg-card border border-dashed border-border rounded-lg p-6 text-center" data-testid="groups-empty">
          <p className="text-sm text-muted-foreground">{myState ? `No chapters cover your area (${myState}) yet.` : "Add your address to see chapters in your area — or browse them all."}</p>
          <button data-testid="groups-show-all-empty" onClick={() => setShowAll(true)} className="mt-3 inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90 transition-colors"><Globe2 className="h-4 w-4" /> Show all groups</button>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {visible.map((g) => (
          <div key={g.id} data-testid={`group-card-${g.id}`} onClick={() => setOpen(g.id)} className="relative cursor-pointer bg-card border border-border/70 rounded-xl overflow-hidden shadow-[0_2px_10px_rgba(0,26,75,0.06)] ring-1 ring-transparent p-4 pt-5 hover:shadow-[0_14px_36px_-8px_rgba(0,26,75,0.24)] hover:-translate-y-1 hover:ring-accent/40 hover:border-accent/50 transition-all duration-200">
            <span className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${g.type === "chapter" ? "from-accent to-accent/40" : "from-secondary to-secondary/40"}`} />
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5">
              <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${g.type === "chapter" ? "bg-accent/20 text-primary" : "bg-secondary/10 text-secondary"}`}>{g.type}</span>
              <span className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {g.member_count}</span>
                <span className="flex items-center gap-1"><CalendarHeart className="h-3.5 w-3.5" /> {g.event_count}</span>
                {g.is_leader && <span className="flex items-center gap-1 text-accent"><Star className="h-3.5 w-3.5" /> Leader</span>}
              </span>
            </div>
            <div className="font-serif text-lg tracking-tight mt-1.5">{g.name}</div>
            {(g.states && g.states.length ? g.states.join(", ") : g.location) && <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1"><MapPin className="h-3 w-3" /> {g.states && g.states.length ? g.states.join(", ") : g.location}</div>}
            {g.description && <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{g.description}</p>}
            <button data-testid={`group-join-${g.id}`} onClick={(e) => toggle(g, e)}
              className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${g.is_member ? "bg-muted text-foreground hover:bg-muted/70" : "bg-primary text-white hover:bg-primary/90"}`}>
              {g.is_member ? <><Check className="h-4 w-4" /> Joined</> : <><Plus className="h-4 w-4" /> Join</>}
            </button>
          </div>
        ))}
      </div>
      {open && <GroupModal id={open} onClose={() => setOpen(null)} onChanged={load} />}
    </section>
  );
}

function GroupModal({ id, onClose, onChanged }) {
  const [g, setG] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const load = () => api.get(`/groups/${id}`).then((r) => setG(r.data)).catch(() => onClose());
  useEffect(() => { load(); }, [id]);

  const toggle = async () => {
    try { await api.post(`/groups/${id}/${g.is_member ? "leave" : "join"}`); load(); onChanged && onChanged(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return createPortal((
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-lg rounded-lg shadow-2xl my-8" onMouseDown={(e) => e.stopPropagation()} data-testid="group-detail-modal">
        {!g ? <div className="p-8 text-muted-foreground">Loading…</div> : (
          <>
            <div className="bg-[#001A4B] text-white rounded-t-lg px-5 py-4 flex items-start justify-between">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-[#C8A24C]">{g.type}</span>
                <div className="font-serif text-xl mt-0.5">{g.name}</div>
                {(g.states && g.states.length ? g.states.join(", ") : g.location) && <div className="text-xs text-white/70 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {g.states && g.states.length ? g.states.join(", ") : g.location}</div>}
              </div>
              <button data-testid="group-modal-close" onClick={onClose} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              {g.description && <p className="text-sm text-muted-foreground">{g.description}</p>}
              <button data-testid="group-modal-join" onClick={toggle} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium ${g.is_member ? "bg-muted hover:bg-muted/70" : "bg-primary text-white hover:bg-primary/90"}`}>
                {g.is_member ? <><Check className="h-4 w-4" /> You're a member — Leave</> : <><Plus className="h-4 w-4" /> Join this group</>}
              </button>
              {g.is_leader && <button data-testid="group-email-leader" onClick={() => setEmailing(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-md bg-secondary text-white text-sm font-medium hover:brightness-110"><Mail className="h-4 w-4" /> Email this group (you're a leader)</button>}
              {emailing && <GroupEmailModal id={id} name={g.name} onClose={() => setEmailing(false)} />}
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><CalendarHeart className="h-3.5 w-3.5" /> Events</div>
                {g.events.length === 0 ? <div className="text-sm text-muted-foreground">No events scheduled yet.</div> : (
                  <ul className="space-y-2">{g.events.map((e) => (
                    <li key={e.id} className="border border-border rounded-md px-3 py-2">
                      <div className="font-medium text-sm">{e.title}</div>
                      <div className="text-xs text-muted-foreground">{fmt(e.date)}{e.location ? ` · ${e.location}` : ""}</div>
                      {e.description && <div className="text-xs text-muted-foreground mt-1">{e.description}</div>}
                    </li>
                  ))}</ul>
                )}
              </div>
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Members ({g.members.length})</div>
                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                  {g.members.map((m) => (
                    <span key={m.id} className="text-xs px-2 py-1 rounded-full bg-muted flex items-center gap-1">{m.is_leader && <Star className="h-3 w-3 text-accent" />}{m.name}{m.class_year ? ` ’${String(m.class_year).slice(-2)}` : ""}</span>
                  ))}
                  {g.members.length === 0 && <span className="text-sm text-muted-foreground">No members yet — be the first!</span>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ), document.body);
}


export function GroupEmailModal({ id, name, onClose }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("Dear {first_name},\n\n");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${id}/email`, { subject, body });
      toast.success(`Sent to ${data.sent} member${data.sent === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`);
      onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return createPortal((
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 bg-slate-900/60 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-card w-full max-w-lg rounded-lg shadow-2xl my-8" onMouseDown={(e) => e.stopPropagation()} data-testid="group-email-modal">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-primary" /><span className="font-serif text-lg">Email {name}</span></div>
          <button data-testid="group-email-close" onClick={onClose}><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Subject</span>
            <input data-testid="group-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="ud-input mt-1" placeholder={`A message for ${name}`} /></label>
          <label className="block"><span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Message</span>
            <textarea data-testid="group-email-body" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="ud-input mt-1 font-sans" />
            <span className="text-[11px] text-muted-foreground mt-1 block">Use <code>{"{first_name}"}</code> and <code>{"{group}"}</code>. Opted-out members are skipped; an unsubscribe link is added.</span></label>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
          <button data-testid="group-email-send" onClick={send} disabled={busy} className="flex items-center gap-2 px-4 py-2 rounded-md bg-secondary text-white text-sm font-medium hover:brightness-110 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
